import type {
  Appointment,
  AppointmentStatus,
  LocalDateTime,
  Location,
  LocationChoice,
  Patient,
} from '@ergo/shared';
import { VISIO_LOCATION } from '@ergo/shared';
import { api } from './api';
import type { SeriesScope } from './components/ScopeDialog';
import { dateOf, formatDuration, timeOf, weekdayOf } from './time';

export const STATUS_LABELS: Record<AppointmentStatus, string> = {
  planned: 'Prévue',
  done: 'Effectuée',
  cancelled: 'Annulée',
  no_show: 'Pas venu·e',
};

/**
 * Ce que dit une séance effectuée, partout de la même façon : sa durée, et le mot
 * qui vaut son montant quand elle a été offerte.
 */
export function doneLabel(actualDurationMin: number | null, free: boolean): string {
  return `${formatDuration(actualDurationMin ?? 0)}${free ? ' · gratuit' : ''}`;
}

/**
 * Le nom d'une séance — « ★ ESS – André » : l'étoile, l'intitulé, le patient. Il se
 * fabrique dans `shared`, parce que le titre de la notification poussée est le même
 * mot que celui de la grille ; le gras et le brun chaud viennent du CSS.
 */
export { titleOf } from '@ergo/shared';

export const VISIO_GLYPH = '💻';

/** Les lieux où elle se rend. La visio n’en est pas un : chaque patient l’a d’office. */
export function placesOf(patient: Pick<Patient, 'locations'>): Location[] {
  return patient.locations.filter((location) => location.kind !== 'visio');
}

/** Ce que le champ « Lieu » montre pour un lieu enregistré : un lieu `visio` est la visio. */
export function locationChoiceOf(
  patient: Pick<Patient, 'locations'>,
  locationId: number,
): LocationChoice {
  const location = patient.locations.find((item) => item.id === locationId);
  return location?.kind === 'visio' ? VISIO_LOCATION : locationId;
}

/**
 * Le « Lieu » d’un patient tout juste choisi : celui d’avant s’il est encore à lui,
 * sinon son premier lieu, et la visio quand il n’en a aucun — elle se planifie toujours.
 */
export function defaultLocationChoice(
  patient: Pick<Patient, 'locations'>,
  current: LocationChoice | null,
): LocationChoice {
  const places = placesOf(patient);
  if (current === VISIO_LOCATION || places.some((place) => place.id === current)) {
    return current ?? VISIO_LOCATION;
  }
  return places[0]?.id ?? VISIO_LOCATION;
}

/** A stable colour per patient, so a face is recognisable across the week grid. */
export function patientHue(patientId: number): number {
  return (patientId * 137) % 360;
}

/** The "Fréquence" choices of the RDV form, in weeks between two séances. */
export const FREQUENCY_OPTIONS = [1, 2, 3, 4].map((intervalWeeks) => ({
  intervalWeeks,
  label: intervalWeeks === 1 ? 'Toutes les semaines' : `Toutes les ${intervalWeeks} semaines`,
}));

/** Qualifies "série": "série hebdomadaire", "série toutes les 2 semaines". */
export function frequencyLabel(intervalWeeks: number): string {
  return intervalWeeks === 1 ? 'hebdomadaire' : `toutes les ${intervalWeeks} semaines`;
}

export interface SlotChange {
  startsAt: LocalDateTime;
  plannedDurationMin?: number;
  locationId?: LocationChoice;
  /** Series only, and only under "toutes les séances à venir"; omitted, the series keeps its own. */
  intervalWeeks?: number;
}

/**
 * A single occurrence is a plain PATCH; "toutes les séances à venir" closes the
 * series at this date and recreates it on the new slot.
 */
export async function applySlotChange(
  appointment: Appointment,
  change: SlotChange,
  scope: SeriesScope,
): Promise<void> {
  const { intervalWeeks, ...single } = change;

  if (scope === 'one' || appointment.seriesId === null) {
    await api.appointments.update(appointment.id, single);
    return;
  }

  // The server cuts the old series and starts the new one at the same date. When the
  // séance moves to an EARLIER day, that date must be the drop date — otherwise the new
  // weekday's first occurrence lands only next week and the current week loses its séance.
  const originalDate = dateOf(appointment.startsAt);
  const newDate = dateOf(change.startsAt);
  await api.series.replace(appointment.seriesId, {
    effectiveFrom: newDate < originalDate ? newDate : originalDate,
    weekday: weekdayOf(newDate),
    startTime: timeOf(change.startsAt),
    durationMin: change.plannedDurationMin,
    locationId: change.locationId,
    intervalWeeks,
  });
}

export async function removeAppointment(
  appointment: Appointment,
  scope: SeriesScope,
): Promise<void> {
  if (scope === 'one' || appointment.seriesId === null) {
    await api.appointments.remove(appointment.id);
    return;
  }
  await api.series.remove(appointment.seriesId, { from: dateOf(appointment.startsAt) });
}

/** Dans la grille de la semaine, la place manque : le prénom seul (jusqu’au premier espace). */
export function firstWordOf(name: string): string {
  return name.trim().split(/\s+/)[0] ?? name;
}

